/* Merging fresh marks into the set already on screen.
 *
 * Kept apart from the store so the §3.3 promise — a failed fetch keeps the mark
 * it already had — can be tested without a browser. */

import type { Quote } from '../types';

/** Whatever came back replaces its symbol; every other symbol keeps the mark it
    already had, including its original fetchedAt so the age line stays honest. */
export function mergeMarks(existing: Quote[], incoming: Quote[]): Quote[] {
  if (incoming.length === 0) return existing;
  const next = new Map(existing.map((q) => [q.symbol, q]));
  for (const q of incoming) next.set(q.symbol, q);
  return [...next.values()];
}

/* The oldest mark, which is what the "prices from 2h ago" line reads — a single
 * stale symbol should not hide behind fresher neighbours.
 *
 * But only among the symbols asked for. The quote set keeps whatever it has
 * ever held: a company sold and removed, a name taken off the radar, a ticker
 * looked up in the search box and never added. Nothing fetches those again, so
 * their timestamp is frozen, and reading the oldest across all of them meant
 * the line under the book's value climbed by an hour every hour and never
 * reset — while the prices behind that value were refreshing perfectly.
 *
 * The line sits beneath the total. It has to describe the marks the total is
 * made of, and an orphan speaks for nothing. */
export function oldestMarkAt(quotes: Quote[], symbols?: Iterable<string>): string | undefined {
  const wanted = symbols === undefined ? null : new Set(symbols);
  const times = quotes
    .filter((q) => wanted === null || wanted.has(q.symbol))
    .map((q) => q.fetchedAt)
    .filter((t): t is string => typeof t === 'string' && t !== '');
  if (times.length === 0) return undefined;
  return times.sort()[0];
}

/* Which symbol is holding the age line back, when one of them is.
 *
 * "Prices from 16h ago" with no name attached is unanswerable: everything on
 * screen looks current, the total moves, and the line insists something is
 * wrong without saying what. It is worth naming only when one listing is
 * genuinely lagging the rest — after a night with the app closed every mark is
 * equally old, and pointing at one of them would be picking a scapegoat.
 *
 * So: named only when the oldest mark is more than an hour behind the next
 * oldest, which is the shape of one symbol that has stopped answering rather
 * than a book that has simply been sitting shut. */
const LAGGING_MS = 60 * 60_000;

export function laggingSymbol(quotes: Quote[], symbols: Iterable<string>): string | undefined {
  const wanted = new Set(symbols);
  const rows = quotes
    .filter((q) => wanted.has(q.symbol) && typeof q.fetchedAt === 'string')
    .sort((a, b) => a.fetchedAt.localeCompare(b.fetchedAt));
  if (rows.length < 2) return undefined;

  const oldest = rows[0]!;
  const next = rows[1]!;
  const gap = new Date(next.fetchedAt).getTime() - new Date(oldest.fetchedAt).getTime();
  return gap > LAGGING_MS ? oldest.symbol : undefined;
}

/** The quotes still worth keeping: those for symbols the book asks about.
    Everything else is a leftover, and a leftover that can never be refreshed is
    one the store has no reason to carry. */
export function pruneMarks(quotes: Quote[], symbols: Iterable<string>): Quote[] {
  const wanted = new Set(symbols);
  return quotes.filter((q) => wanted.has(q.symbol));
}
